'use client';

import type { User } from '@/types/user/type';
import { VpIcon, VpIconButton } from '@vtmn-play/react/headless';
import { useTranslations } from 'next-intl';
import { updateUserMedia } from '@/app/actions/server';
import { FileUploader } from '@/app/components/FileUploader/FileUploader';
import { Loader } from '@/app/components/Loader';
import { useApiErrorContext, useAuthenticatedContext } from '@/app/components/Provider';
import { Avatar } from '~/components/commons/Avatar/Avatar';
import { CustomSwipeableDrawer } from '~/components/commons/CustomSwipeableDrawer/CustomSwipeableDrawer';
import { Portal } from '~/components/commons/Portal/Portal';
import { apiPaths } from '~/const/api';
import { useRequestState } from '~/hooks/request/useRequestState';

export const ProfilePictureDialog = ({
  isSBreakpoint,
  handleClose,
}: {
  isSBreakpoint: boolean;
  handleClose: () => void;
}) => {
  const t = useTranslations();

  const { user, setUser } = useAuthenticatedContext();
  const { setError } = useApiErrorContext();
  const { status, startRequest, handleSuccess, handleError } = useRequestState();

  const handleChange = async (formData: FormData) => {
    startRequest();
    const response = await updateUserMedia(user.id, formData);

    if (response['hydra:description']) {
      handleError();
      return setError(response);
    }

    setUser(response as User);
    handleSuccess();
  };

  return isSBreakpoint
    ? (
        <CustomSwipeableDrawer
          dataTestId="profile-picture-drawer"
          open
          title={t('PROFILE.PICTURE.DIALOG.TITLE')}
          onClose={handleClose}
        >
          <Loader loading={status === 'pending'} size="medium">
            <div className="profile-picture-dialog__container__content">
              <Avatar user={user} avatarSize={120} />
              <span className="vp-title-s">{user?.firstname}</span>
              <div className="profile__avatar-editing" data-testid="editing-mode">
                <FileUploader
                  handleInputFileChange={handleChange}
                  inputLabel={t('PROFILE.PICTURE.DIALOG.ACTION')}
                  path={apiPaths.USER}
                  id="user-avatar"
                  isPicture
                  fromProfile
                />
              </div>
            </div>
          </Loader>
        </CustomSwipeableDrawer>
      )
    : (
        <Portal>
          <div data-testid="profile-picture-dialog" className="dialog__overlay">
            <div className="modal show">
              <div className="modal-dialog">
                <div className="profile-picture-dialog__container">
                  <Loader loading={status === 'pending'} size="medium">
                    <div className="modal-inner-wrapper">
                      <div className="profile-picture-dialog__container__header">
                        <div className="modal-title vp-title-m">
                          {t('PROFILE.PICTURE.DIALOG.TITLE')}
                        </div>
                        <VpIconButton
                          aria-label={t('CONTACT_DIALOG.CLOSE')}
                          variant="tertiary"
                          onClick={handleClose}
                        >
                          <VpIcon name="close" />
                        </VpIconButton>
                      </div>
                      <div className="profile-picture-dialog__container__content">
                        <Avatar user={user} avatarSize={120} />
                        <span className="vp-title-s">{user?.firstname}</span>
                        <div
                          className="profile__avatar-editing"
                          data-testid="editing-mode"
                        >
                          <FileUploader
                            handleInputFileChange={handleChange}
                            inputLabel={t('PROFILE.PICTURE.DIALOG.ACTION')}
                            path={apiPaths.USER}
                            id="user-avatar"
                            isPicture
                            fromProfile
                          />
                        </div>
                      </div>
                    </div>
                  </Loader>
                </div>
              </div>
            </div>
          </div>
        </Portal>
      );
};
